import { useRef, useEffect, useContext, useMemo, useState } from 'react';
import { Settings } from 'react-feather';
import Select from 'react-select';
import styled from '@emotion/styled';
import { useFloating, useInteractions, useClick, useDismiss } from '@floating-ui/react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { Bar, BarChart, Line, PieChart } from '@trinity/chat';
import { ChartThemePicker } from '@trinity/chat-components/components/ChartTheming';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { BrandThemingContext } from '@trinity/chat-components/containers/Branding/Context';
import { UpdateChartPreferencesContext } from '@trinity/chat-components/containers/ChartPreferences/context';
import * as Presentation from '../Chat.Filters/Presentation';
import Input from '../Input';
import { ChartFilterButton } from './Chat.Chart.Filter.Button';
import { useChartFilters, type FilterRow } from './Chat.Chart.Filter.Context';
import { ChartFilterMenu } from './Chat.Chart.Filter.Menu';
import { useActiveChartFiltersCount } from './Chat.Chart.Filter.utils';
import type { ChartType } from './Chat.Chart.utils';
import { mapChartTypeToBackend } from './Chat.Chart.utils';
import { ChartGroupByContext, ChartInfoContext, ChartTypeContext, ChartRecordIdContext, ChartThemeIdContext } from './context';
import { barToPie, pieToBar } from './Message.Response.ChartContainer';

// Data conversion function to transform various chart data types to ChartDataPoint[]
function isPieData(data: unknown[]): data is { id: string; label: string; value: number }[] {
  return data.length > 0 && typeof data[0] === 'object' && data[0] !== null && 'id' in data[0] && 'label' in data[0];
}

function isBarData(data: unknown[]): data is { x: string; y: number }[] {
  return data.length > 0 && typeof data[0] === 'object' && data[0] !== null && 'x' in data[0] && 'y' in data[0] && !('id' in data[0]);
}

function isMultipleBarData(data: unknown[]): data is Bar[] {
  return data.length > 0 && typeof data[0] === 'object' && data[0] !== null && 'index' in data[0] && 'data' in data[0];
}

function isSingleLineData(data: unknown): data is Line {
  return !Array.isArray(data) && data != null && typeof data === 'object' && 'id' in data && 'data' in data;
}

function isMultipleLineData(data: unknown[]): data is Line[] {
  return data.length > 0 && typeof data[0] === 'object' && data[0] !== null && 'id' in data[0] && 'data' in data[0];
}

function convertToChartDataPoints(
  data: { id: string; label: string; value: number }[] |
    { x: string; y: number }[] |
    Bar[] |
    Line |
    Line[],
): { id?: string; label?: string; x?: string; value?: number; y?: number }[] {
  if (!data) return [];

  if (Array.isArray(data)) {
    if (isPieData(data)) return data;
    if (isBarData(data)) return data;
    if (isMultipleBarData(data)) {
      return data.flatMap(bar =>
        bar.data.map(point => ({ x: point.x, y: point.y, label: bar.index })),
      );
    }
    if (isMultipleLineData(data)) {
      return data.flatMap(line =>
        line.data.map(point => ({
          x: typeof point.x === 'string' ? point.x : point.x.toString(),
          y: point.y,
          label: line.id,
        })),
      );
    }
  }

  if (isSingleLineData(data)) {
    return data.data.map(point => ({
      x: typeof point.x === 'string' ? point.x : point.x.toString(),
      y: point.y,
      label: data.id,
    }));
  }

  return [];
}

function getSelectedLabels(filters: FilterRow[]): string[] {
  return filters
    .filter(filterRow => filterRow.selectedOption?.value === 'selected')
    .flatMap(filterRow => (filterRow.valueOption ?? []).map(option => option.value));
}

function getNotSelectedLabels(filters: FilterRow[]): string[] {
  return filters
    .filter(filterRow => filterRow.selectedOption?.value === 'not_selected')
    .flatMap(filterRow => (filterRow.valueOption ?? []).map(option => option.value));
}

function applyLabelFilters<T>(data: T[], labelAccessor: (item: T) => string, filters: FilterRow[]): T[] {
  const selectedLabels = getSelectedLabels(filters);
  const notSelectedLabels = getNotSelectedLabels(filters);
  let result = data;
  if (selectedLabels.length > 0) {
    result = result.filter(item => selectedLabels.includes(labelAccessor(item)));
  }
  if (notSelectedLabels.length > 0) {
    result = result.filter(item => !notSelectedLabels.includes(labelAccessor(item)));
  }
  return result;
}

function applyChartFilters(chartType: string, originalData: unknown, filters: FilterRow[]): unknown {
  if (chartType === 'pie') {
    const data = (originalData as { id: string; label: string; value: number }[]).filter(
      dp => typeof dp === 'object' && dp !== null && typeof dp.label === 'string',
    );
    return applyLabelFilters(data, dp => dp.label, filters);
  }
  if (chartType === 'bar' || chartType === 'stacked-bar') {
    const data = (originalData as { x: string; y: number }[]).filter(
      dp => typeof dp === 'object' && dp !== null && typeof dp.x === 'string',
    );
    return applyLabelFilters(data, dp => dp.x, filters);
  }
  return originalData;
}

function computeFilteredChartData(chartTypeLocal: string, originalData: unknown, filters: FilterRow[]): { shouldReset: boolean; filteredData: unknown } {
  const hasAnySelection = filters.some(
    filterRow => Array.isArray(filterRow.valueOption) && filterRow.valueOption.length > 0,
  );
  if (!hasAnySelection) {
    return { shouldReset: true, filteredData: originalData };
  }
  const filteredData = applyChartFilters(chartTypeLocal, originalData, filters);
  const isSupported = ['pie', 'donut', 'bar', 'stacked-bar'].includes(chartTypeLocal);
  return { shouldReset: false, filteredData: isSupported ? filteredData : null };
}

function applyFilteredDataToChart(chartTypeLocal: string, originalData: unknown, filters: FilterRow[], setChart: (fn: (prev: any) => any) => void, chart: any) {
  const { shouldReset, filteredData } = computeFilteredChartData(chartTypeLocal, originalData, filters);

  if (shouldReset) {
    setChart((prev: typeof chart) => ({
      ...prev,
      chart: {
        ...prev.chart,
        chart: {
          ...prev.chart.chart,
          data: originalData,
        },
      },
    }));
    return;
  }

  if (filteredData) {
    setChart((prev: typeof chart) => ({
      ...prev,
      chart: {
        ...prev.chart,
        chart: { ...prev.chart.chart, data: filteredData as typeof prev.chart.chart.data },
      },
    }));
  }
}

function ChartFilterSection({ filterRefs, getFilterReferenceProps, filterOpen, setFilterOpen, isFilterSupported, activeFiltersCount, filterFloatingStyles, getFilterFloatingProps, chart, filters, originalDataRef }: {
  filterRefs: any;
  getFilterReferenceProps: any;
  filterOpen: boolean;
  setFilterOpen: (v: boolean | ((o: boolean) => boolean)) => void;
  isFilterSupported: boolean;
  activeFiltersCount: number;
  filterFloatingStyles: any;
  getFilterFloatingProps: any;
  chart: any;
  filters: FilterRow[];
  originalDataRef: React.RefObject<any>;
}) {
  return (
    <>
      <span
        ref={filterRefs.setReference}
        {...getFilterReferenceProps()}
        style={{ display: 'flex', alignItems: 'center' }}>
        <ChartFilterButton
          onClick={() => setFilterOpen(open => !open)}
          disabled={!isFilterSupported}
          filtersCount={activeFiltersCount} />
      </span>
      {filterOpen && isFilterSupported && (
        <PopperRoot
          ref={filterRefs.setFloating}
          style={filterFloatingStyles}
          {...getFilterFloatingProps()}>
          <PopperHeader>Chart Filters</PopperHeader>
          <PopperContent>
            <ChartFilterMenu
              chartInfo={{ ...chart, filters }}
              originalData={convertToChartDataPoints(originalDataRef.current)} />
          </PopperContent>
        </PopperRoot>
      )}
    </>
  );
}

function ChartSettingsMenu({ isOpen, refs, floatingStyles, getFloatingProps, selectedChartType, setSelectedChartType, selectedThemeId, setSelectedThemeId, brandThemingData, brandThemingQuery }: {
  isOpen: boolean;
  refs: any;
  floatingStyles: any;
  getFloatingProps: any;
  selectedChartType: string;
  setSelectedChartType: (v: string) => void;
  selectedThemeId: number | null;
  setSelectedThemeId: (v: number | null) => void;
  brandThemingData: any;
  brandThemingQuery: any;
}) {
  if (!isOpen) return null;
  const groupingDisabled = selectedChartType !== 'stacked-bar' && selectedChartType !== 'bar' && selectedChartType !== 'pie';
  return (
    <Root
      ref={refs.setFloating}
      style={floatingStyles}
      {...getFloatingProps()}>
      <Header>
        <HeaderLabel>Chart Settings</HeaderLabel>
      </Header>
      <Filters>
        <ChartTypeController
          selectedChartType={selectedChartType}
          onChange={setSelectedChartType} />

        <LabelContainer>
          <Label>Group small values into &quot;Others&quot;</Label>
          <SubLabel> Only applies when chart data contains small categories </SubLabel>
        </LabelContainer>

        <GroupController disabled={groupingDisabled} />

        {groupingDisabled && (
          <ErrorContainer>
            <StyledErrorIcon />
            <ErrorMessage> &quot;Others grouping&quot; is not available for this chart type or data set.</ErrorMessage>
          </ErrorContainer>
        )}
      </Filters>

      <Divider />

      <ChartThemePicker
        selectedThemeId={selectedThemeId ?? null}
        onThemeSelect={setSelectedThemeId}
        categories={brandThemingData?.categories ?? []}
        isLoading={brandThemingQuery?.isLoading ?? false} />
    </Root>
  );
}

export function ChartFilterPane() {
  const [isOpen, setIsOpen] = useState(false);
  const [chart, setChart] = useContext(ChartInfoContext);
  const [selectedChartType, setSelectedChartType] = useContext(ChartTypeContext);
  const [selectedThemeId, setSelectedThemeId] = useContext(ChartThemeIdContext);
  const { data: brandThemingData, query: brandThemingQuery } = useContext(BrandThemingContext);
  const [filterOpen, setFilterOpen] = useState(false);

  // Supported chart types for V1 filter panel
  const SUPPORTED_FILTER_CHART_TYPES_V1 = ['pie', 'donut', 'bar', 'stacked-bar'];

  const isFilterSupported = SUPPORTED_FILTER_CHART_TYPES_V1.includes(selectedChartType);

  // FILTER MENU FLOATING LOGIC
  const {
    refs: filterRefs,
    floatingStyles: filterFloatingStyles,
    context: filterContext,
  } = useFloating({
    open: filterOpen,
    onOpenChange: setFilterOpen,
    placement: 'bottom-start',
  });

  const filterClick = useClick(filterContext);
  const filterDismiss = useDismiss(filterContext);
  const { getFloatingProps: getFilterFloatingProps, getReferenceProps: getFilterReferenceProps } = useInteractions([filterClick, filterDismiss]);

  // SETTINGS MENU FLOATING LOGIC
  const { context, refs, floatingStyles } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const originalDataRef = useRef<
    { id: string; label: string; value: number }[] | // PieChart
    { x: string; y: number }[] |                    // BarPoints (BarChart)
    Bar[] |                                    // MultipleBarChart
    Line |                                     // LineChart
    Line[]                                     // MultipleLineChart
  >(chart.chart.chart.data);

  const chartType: string = chart?.chart?.type || 'unknown';
  const chartId = `${chart.quantItemId}_${chartType}`;

  // Get the active filters count for this chart
  const activeFiltersCount = useActiveChartFiltersCount(chartId);

  // Get filters from context (now properly typed)
  const chartFiltersMap = useChartFilters();
  const filters = chartFiltersMap[chartId] ?? [];

  const handleFiltersChange = (filters: FilterRow[]) => {
    const originalData = originalDataRef.current;
    const chartTypeLocal: string = chart.chart.type;
    applyFilteredDataToChart(chartTypeLocal, originalData, filters, setChart, chart);
  };

  useEffect(() => {
    if (filters && filters.length > 0) {
      handleFiltersChange(filters);
    }
  }, [filters, chartId, chartType]);

  const { getFloatingProps, getReferenceProps } = useInteractions([click, dismiss]);

  return (
    <TopRow>
      <ChartFilterSection
        filterRefs={filterRefs}
        getFilterReferenceProps={getFilterReferenceProps}
        filterOpen={filterOpen}
        setFilterOpen={setFilterOpen}
        isFilterSupported={isFilterSupported}
        activeFiltersCount={activeFiltersCount}
        filterFloatingStyles={filterFloatingStyles}
        getFilterFloatingProps={getFilterFloatingProps}
        chart={chart}
        filters={filters}
        originalDataRef={originalDataRef}
      />

      {/* SETTINGS BUTTON */}
      <span
        ref={refs.setReference}
        {...getReferenceProps()}
        style={{ display: 'flex', alignItems: 'center' }}>
        <StyledSettingsIcon />
      </span>

      {/* SETTINGS MENU */}
      <ChartSettingsMenu
        isOpen={isOpen}
        refs={refs}
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        selectedChartType={selectedChartType}
        setSelectedChartType={setSelectedChartType}
        selectedThemeId={selectedThemeId}
        setSelectedThemeId={setSelectedThemeId}
        brandThemingData={brandThemingData}
        brandThemingQuery={brandThemingQuery}
      />
    </TopRow>
  );
}

const Divider = styled.div({
  height: '1px',
  backgroundColor: '#e0e0e0',
  margin: '16px 0',
});

const TopRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

function ChartTypeController({ selectedChartType, onChange }: { selectedChartType: string | null; onChange: (value: string | null) => void }) {
  const [, setChart] = useContext(ChartInfoContext);
  const chartRecordId = useContext(ChartRecordIdContext);
  const overrideTypesRef = useRef<string[]>([]);
  const theme = useTheme();

  const updateChartRecordOverride = useContext(UpdateChartPreferencesContext);

  const selectStyles = useMemo(() => Presentation.buildSelectStyles(theme), [theme]);
  const selectTheme = useMemo(() => Presentation.buildSelectTheme(theme), [theme]);

  const filteredOptions = useMemo(() => {

    const singleOptions = [
      { value: 'bar', label: 'Bar' },
      { value: 'pie', label: 'Pie' },
      { value: 'stacked-bar', label: 'Singular Stacked Bar' },
    ];

    const multipleOptions = [
      { value: 'multiple-stacked-bar', label: 'Multiple Stacked Bar' },
      { value: 'multiple-clustered-bar', label: 'Multiple Clustered Bar' },
    ];

    if (singleOptions.map(option => option.value).includes(selectedChartType)) {
      return singleOptions;
    }
    else {
      return multipleOptions;
    }
  }, [selectedChartType]);
  const handleChange = (option: { value: string }) => {
    const newChartType = option.value;

    // Record the chart type override
    const recordChartOverride = async () => {
      if (chartRecordId && selectedChartType !== newChartType) {
        try {
          const mappedChartType = mapChartTypeToBackend(newChartType);
          if (mappedChartType) {
            overrideTypesRef.current = [mappedChartType];
            await updateChartRecordOverride({
              recordId: chartRecordId,
              overrideChartTypes: overrideTypesRef.current as ChartType[],
            });
          }
        } catch (error) {
          console.warn('Failed to record chart type override:', error);
        }
      }
    };

    if ((selectedChartType === 'bar' || selectedChartType === 'stacked-bar') && newChartType === 'pie') {
      setChart(prev => ({
        ...prev,
        chart: {
          ...prev.chart,
          type: newChartType,
          chart: barToPie(prev.chart.chart as BarChart),
        },
      }));
    }

    else if (selectedChartType === 'pie' && (newChartType === 'bar' || newChartType === 'stacked-bar')) {
      setChart(prev => ({
        ...prev,
        chart: {
          ...prev.chart,
          type: newChartType,
          chart: pieToBar(prev.chart.chart as PieChart),
        },
      }));
    }

    else if (selectedChartType === 'multiple-stacked-bar' && newChartType === 'multiple-clustered-bar') {
      setChart(prev => ({
        ...prev,
        chart: {
          ...prev.chart,
          type: newChartType,
          chart: prev.chart.chart,
        },
      }));
    }

    else if (selectedChartType === 'multiple-clustered-bar' && newChartType === 'multiple-stacked-bar') {
      setChart(prev => ({
        ...prev,
        chart: {
          ...prev.chart,
          type: newChartType,
          chart: prev.chart.chart,
        },
      }));
    }

    onChange(newChartType);
    recordChartOverride();
  };

  return (
    <ChartTypeContainer>
      <Label>Chart Type</Label>
      <Select
        options={filteredOptions}
        id="chart-type"
        value={filteredOptions.find(option => option.value === selectedChartType)}
        onChange={handleChange}
        styles={selectStyles}
        theme={selectTheme} />
    </ChartTypeContainer>
  );
}

function GroupController({ disabled }: { disabled: boolean }) {
  const [groupBy, setGroupBy] = useContext(ChartGroupByContext);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setGroupBy(+value);
  };

  return (
    <GroupContainer>
      <Label>Show Top N Categories:</Label>
      <Input
        id="group-values-below"
        placeholder="Input Number"
        type="number"
        value={groupBy || ''}
        disabled={disabled}
        onChange={handleInputChange} />
    </GroupContainer>
  );
}

const PopperRoot = styled.div(({ theme }) => ({
  background: theme?.palette?.white?.main || '#fff',
  border: `1px solid ${theme?.palette?.gray?.light1 || '#e0e0e0'}`,
  borderRadius: 6,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  minWidth: 280,
  zIndex: 1000,
  marginTop: 4,
}));

const PopperHeader = styled.div(({ theme }) => ({
  fontWeight: 600,
  fontSize: 16,
  padding: '12px 16px 8px 16px',
  borderBottom: `1px solid ${theme?.palette?.gray?.light2 || '#f0f0f0'}`,
}));

const PopperContent = styled.div({
  padding: '12px 16px',
});

const Root = styled.div(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: '5px',
  border: `1px solid ${theme.palette.gray.light1}`,
  boxShadow: '0 2px 6px 2px rgba(60, 64, 67, .15)',
  boxSizing: 'border-box',
  padding: '12px',
  minWidth: 280,
  zIndex: 1000,
}));

const Header = styled.div({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '10px',
});

const HeaderLabel = styled.h1(({ theme }) => ({
  fontSize: '18px',
  fontFamily: theme.fonts.semiBold,
}));

const Filters = styled.div({
  boxSizing: 'border-box',
  padding: '0 2px',
});

const StyledErrorIcon = styled(ErrorOutlineIcon)({
  color: 'red',
  fontSize: '12px',
  marginLeft: '5px',
});

const ErrorMessage = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.regular,
  color: 'red',
  fontSize: '10px',
}));

const ErrorContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
});

const ChartTypeContainer = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '5px',
  marginBottom: '10px',
});

const GroupContainer = styled.div({
  display: 'flex',
  whiteSpace: 'nowrap',
  alignItems: 'center',
  gap: '2px',
  marginBottom: '10px',
});

const Label = styled.label(({ theme }) => ({
  whiteSpace: 'nowrap',
  fontFamily: theme.fonts.semiBold,
  fontSize: '14px',
}));

const SubLabel = styled.label(({ theme }) => ({
  fontFamily: theme.fonts.regular,
  fontSize: '10px',
  color: theme.palette.gray.light1,
}));

const LabelContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  marginBottom: '10px',
});

const StyledSettingsIcon = styled(Settings)(({ theme }) => ({
  fontSize: '12px !important',
  cursor: 'pointer',
  color: 'var(--gray-d)',
}));
